import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { uploadState } from "../lib/uploadState";
import { uploadDirect } from "../lib/uploadDirect";
// Tâche 1451 — formats partagés avec le serveur, et déclaration des refus
// décidés dans le navigateur.
import { ACCEPT_MEDIA, LISTE_MEDIA, estAudio, extensionAcceptee } from "../lib/formatsVideo";
import { signalerRefusNavigateur } from "../lib/refusImport";
import ImportRefusalsPanel from "../components/text/ImportRefusalsPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  CloudArrowUp, VideoCamera, X, SpinnerGap, CheckCircle,
  MusicNote, Waveform, Brain, ShieldCheck, ArrowRight
} from "@phosphor-icons/react";

import { API_BASE as API } from "../lib/apiBase";
// Vincent 2026-07-09 URGENCE PROD — Toast de feedback lors des retries chunks.
import { toast } from "sonner";
// Eric 2026-07-25 iter148 — migration drift : utiliser le helper canonique
// au lieu des checks inline user.role === "admin" || user.org_role === ...
import { canEditContent } from "../utils/permissions";

// Vincent B629 (Eric 2026-07-30 SPEC STRICK PROD) — Thèmes FR-only depuis
// la source unique lib/trainingThemes.js. Fin des libellés « Sales »,
// « Compliance », etc. affichés en anglais dans l'UI FR même quand la
// clé i18n aurait dû traduire.
import { TRAINING_THEMES, themeLabelFor } from "../lib/trainingThemes";
import AudioSaturationAlert from "../components/AudioSaturationAlert"; // Tâche 1313

const PIPELINE_STEPS = [
  { key: "step_upload", icon: CloudArrowUp },
  { key: "step_audio", icon: Waveform },
  { key: "step_transcription", icon: MusicNote },
  { key: "step_extraction", icon: Brain },
  { key: "step_validation", icon: ShieldCheck },
];

export default function UploadPage() {
  const { lang, t, tr } = useLang();
  // Tâche 1326 (Eric 2026-06-30) — Les 3 interrupteurs et leur titre
  // débordaient sur une 2e ligne. Formulations raccourcies demandées par
  // Eric. En français « désactivé » perd le mot « bypass » : l'infobulle de
  // chaque bouton continue de dire que l'étape est CONTOURNÉE, pas éteinte.
  const etatSwitch = (coupe) => coupe
    ? tr("désactivé", "OFF (bypass)")
    : tr("activé", "ON (active)");
  const { user } = useAuth();
  const isFounder = !!(user?.is_founder || user?.is_super_admin);
  // Vincent #425 (Eric 2026-07-07 SPEC STRICK) — Kill switches accessibles
  // à toute personne autorisée à éditer un parcours et à importer des
  // vidéos (concepteurs de cours inclus), pas seulement founder/admin.
  // Eric 2026-07-25 — Migration drift : consolidé sur canEditContent()
  // (déjà couvre is_founder, is_super_admin, role=admin, org_role in
  // [admin, concepteur, employee_advanced, ...], access_level=avance).
  const canEditVideos = canEditContent(user);
  // Vincent #422 (Eric 2026-07-06 21h50 SPEC STRICK) — Kill switch AssemblyAI
  // rendu VISIBLE directement sur la page d'upload (Vincent ne le voyait pas
  // sur /dashboard/video-queue). Founder-only.
  // Lot C (Eric 2026-06-30) — `null` = état INCONNU tant que les drapeaux ne
  // sont pas chargés. Avant, l'état partait à `false`, donc l'écran annonçait
  // « Diarisation : ON » alors que l'interrupteur global était coupé : c'est ce
  // mensonge qui a fait croire pendant un mois que la diarisation tournait.
  const [skipWhisper, setSkipWhisper] = useState(null);
  const [skipDiar, setSkipDiar] = useState(null);
  const [skipVisual, setSkipVisual] = useState(null);
  const flagsUnknown = skipWhisper === null || skipDiar === null || skipVisual === null;
  useEffect(() => {
    if (!canEditVideos) return;
    axios.get(`${API}/api/videos/admin/pipeline-flags`, { withCredentials: true })
      .then(({ data }) => {
        setSkipWhisper(!!data?.skip_whisper_transcription);
        setSkipDiar(!!data?.skip_assemblyai_diarization);
        setSkipVisual(!!data?.skip_visual_speaker_id);
      })
      .catch(() => {});
  }, [canEditVideos]);
  // Tâche 1183 (Eric 2026-06-25) — Kill switch « Whisper ». À OFF, le
  // pipeline part de la transcription Fireflies fournie par l'organisation :
  // inutile de payer deux fois la même minute. Les deux autres switches sont
  // alors verrouillés — sans Whisper il n'y a ni diarisation ni
  // identification faciale à piloter.
  const toggleSkipWhisper = async () => {
    if (uploading) {
      toast.error(tr("Impossible de modifier les interrupteurs pendant l'analyse Whisper. Attendez la fin de l'envoi en cours.", "The switches cannot be changed while Whisper is running. Wait for the current upload to finish."));
      return;
    }
    try {
      // Lot D — On envoie la valeur VOULUE : l'inversion implicite laissait la
      // production dans l'état inverse quand un essai était rejoué.
      const { data } = await axios.post(`${API}/api/videos/admin/toggle-skip-whisper`,
        { value: !skipWhisper }, { withCredentials: true });
      setSkipWhisper(!!data?.skip_whisper_transcription);
      if (data?.note) toast.warning(data.note, { duration: 10000 });
      if (data?.message) toast.info(data.message);
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Bascule impossible — réessayez.", "Could not switch — try again."), { duration: 12000 });
    }
  };
  const toggleSkipDiar = async () => {
    if (uploading) {
      toast.error(tr("Impossible de modifier les interrupteurs pendant l'analyse Whisper. Attendez la fin de l'envoi en cours.", "The switches cannot be changed while Whisper is running. Wait for the current upload to finish."));
      return;
    }
    if (skipWhisper) {
      toast.error(tr("Whisper est à OFF : la diarisation et l'identification faciale n'ont plus d'objet. Remettez Whisper à ON pour les régler.", "Whisper is OFF: diarization and facial ID have nothing to work on. Turn Whisper back ON to set them."));
      return;
    }
    try {
      const { data } = await axios.post(`${API}/api/videos/admin/toggle-skip-diarization`,
        { value: !skipDiar }, { withCredentials: true });
      const newSkipDiar = !!data?.skip_assemblyai_diarization;
      setSkipDiar(newSkipDiar);
      if (data?.note) toast.warning(data.note, { duration: 10000 });
      // Vincent B570 (Eric 2026-07-27) — Interdépendance : Diarisation OFF
      // (skipDiar=true) empêche mécaniquement Identification faciale d'être
      // ON. On force skipVisual=true dans ce cas via l'endpoint dédié.
      if (newSkipDiar && !skipVisual) {
        try {
          const { data: v } = await axios.post(`${API}/api/videos/admin/toggle-skip-visual-speaker-id`,
            { value: true }, { withCredentials: true });
          setSkipVisual(!!v?.skip_visual_speaker_id);
          toast.info(tr("Identification faciale désactivée automatiquement — elle dépend de la diarisation.", "Facial ID disabled automatically — it depends on diarization."));
        } catch {
          toast.error(tr("Identification faciale non désactivée — vérifiez son interrupteur.", "Facial ID not disabled — check its switch."));
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Bascule impossible — réessayez.", "Could not switch — try again."), { duration: 12000 });
    }
  };
  const toggleSkipVisual = async () => {
    if (uploading) {
      toast.error(tr("Impossible de modifier les interrupteurs pendant l'analyse Whisper. Attendez la fin de l'envoi en cours.", "The switches cannot be changed while Whisper is running. Wait for the current upload to finish."));
      return;
    }
    if (skipWhisper) {
      toast.error(tr("Whisper est à OFF : la diarisation et l'identification faciale n'ont plus d'objet. Remettez Whisper à ON pour les régler.", "Whisper is OFF: diarization and facial ID have nothing to work on. Turn Whisper back ON to set them."));
      return;
    }
    // Vincent B570 (Eric 2026-07-27) — Bloquer la réactivation d'Identification
    // faciale tant que Diarisation est OFF (dépendance fonctionnelle).
    if (skipVisual && skipDiar) {
      toast.error(tr("Impossible de réactiver l'Identification faciale : la Diarisation est désactivée. Réactivez d'abord la Diarisation.", "Cannot re-enable Facial ID: Diarization is off. Re-enable Diarization first."));
      return;
    }
    try {
      const { data } = await axios.post(`${API}/api/videos/admin/toggle-skip-visual-speaker-id`,
        { value: !skipVisual }, { withCredentials: true });
      setSkipVisual(!!data?.skip_visual_speaker_id);
      if (data?.note) toast.warning(data.note, { duration: 10000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || tr("Bascule impossible — réessayez.", "Could not switch — try again."), { duration: 12000 });
    }
  };
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [theme, setTheme] = useState("general");

  // Vincent bogue 669 (Eric 2026-08-05) — Support query params
  // ?title=&description=&theme= pour pré-remplir le formulaire après un
  // « Supprimer et ré-uploader » depuis la file d'attente (fichier orphelin).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const qtitle = p.get("title");
    const qdesc = p.get("description");
    const qtheme = p.get("theme");
    if (qtitle) setTitle(qtitle);
    if (qdesc) setDescription(qdesc);
    if (qtheme) setTheme(qtheme);
  }, []);
  const [file, setFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(-1);
  const [success, setSuccess] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const [error, setError] = useState("");
  // Vincent bogue 666 (Eric 2026-08-05) — Rapport d'upload : durée totale,
  // taille, mode (chunké/direct), retries et chunks en échec. Affiché sous
  // « Fichier envoyé ! » pour donner à Vincent une vue sur ce qui s'est passé
  // pendant l'envoi (utile en cas d'upload lent ou de retries silencieux).
  const [uploadReport, setUploadReport] = useState(null);

  // Eric 2026-08-05 (direct upload) — Detecte au mount si l'org du user est
  // éligible au flow direct browser→S3. Sinon on garde l'ancien flow chunké.
  const [directUploadEnabled, setDirectUploadEnabled] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/videos/upload/direct/capability`, { withCredentials: true })
      .then((r) => {
        if (!cancelled) setDirectUploadEnabled(!!r.data?.direct_upload_enabled);
      })
      .catch(() => {
        if (!cancelled) setDirectUploadEnabled(false);
      });
    return () => { cancelled = true; };
  }, []);
  const fileInputRef = useRef(null);
  const [usage, setUsage] = useState(null);
  // Vincent #263 (2026-06-11) — Liste À COCHER (sélection MULTIPLE) de parcours
  // à l'upload. Vincent exige explicitement des cases à cocher et non un menu
  // déroulant : on veut pouvoir rattacher la vidéo à PLUSIEURS parcours.
  const [pathways, setPathways] = useState([]);
  const [selectedPathwayIds, setSelectedPathwayIds] = useState([]);
  // Vincent #417 (Eric 2026-07-06 21h50 SPEC STRICK — v2) — Boîte optionnelle
  // Fireflies : l'utilisateur DÉPOSE UN FICHIER (.docx, .txt, .md) qui sera
  // stocké et utilisé UNIQUEMENT à l'étape de fusion des transcriptions.
  // Elle NE REMPLACE JAMAIS Whisper : le pipeline fait toujours sa transcription
  // IA de son côté, puis la fusion combine les 2 sources.
  const [firefliesFile, setFirefliesFile] = useState(null);
  const firefliesInputRef = useRef(null);
  // Vincent XLSX 2026-07-16 Row 499 (Eric 2026-07-16 SPEC STRICK) —
  // Nouveau mode "multi_select" : liste à cocher de locuteurs connus (identique
  // à l'onglet Locuteurs). Envoie `assigned_speaker_ids[]` (pluriel) au backend.
  const [diarizationMode, setDiarizationMode] = useState("multi"); // "multi" | "single" | "multi_select"
  const [knownSpeakers, setKnownSpeakers] = useState([]);
  const [singleSpeakerId, setSingleSpeakerId] = useState("");
  const [multiSelectedSpeakerIds, setMultiSelectedSpeakerIds] = useState(new Set());
  // Use a ref-based depth counter so Chrome's dragenter/dragleave on child
  // elements don't flicker the highlight off → on. Without this, dropping on
  // a child element (icon, label) may briefly clear dragActive and Chrome
  // falls back to its native "open file in tab" behaviour.
  const dragCounterRef = useRef(0);
  const navigate = useNavigate();

  // Suppress browser default behaviour that OPENS the file in the current tab
  // when the user misses the dropzone. Without this, Vincent (Chrome) saw the
  // video file open as a blob URL instead of being uploaded.
  useEffect(() => {
    const blockDefault = (e) => { e.preventDefault(); };
    window.addEventListener("dragover", blockDefault);
    window.addEventListener("drop", blockDefault);
    return () => {
      window.removeEventListener("dragover", blockDefault);
      window.removeEventListener("drop", blockDefault);
    };
  }, []);

  // Fetch current monthly quota so the user sees pressure BEFORE starting a big upload
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/usage/me`, { withCredentials: true });
        if (!cancelled) setUsage(res.data || null);
      } catch (_) {
        // silently ignore — not critical
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Vincent #263 (2026-02-06) — Charger la liste des parcours publiés ET
  // brouillons pour permettre le rattachement direct à l'upload. La liste
  // est filtrée côté backend par org_id (isolation multi-tenant respectée).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/pathways`, { withCredentials: true });
        if (!cancelled) setPathways(Array.isArray(res.data) ? res.data : (res.data?.pathways || []));
      } catch (_) {
        setPathways([]);
      }
      // Vincent #405 + #406 (Eric 2026-07-02 SPEC STRICK) — Liste des locuteurs
      // connus TRIÉE ALPHABÉTIQUEMENT pour le mode "1 seul locuteur connu".
      try {
        const sp = await axios.get(`${API}/api/speakers`, { withCredentials: true });
        const arr = sp.data?.speakers || sp.data || [];
        const sorted = [...arr].sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" })
        );
        if (!cancelled) setKnownSpeakers(sorted);
      } catch (_) {
        if (!cancelled) setKnownSpeakers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter") {
      dragCounterRef.current += 1;
      setDragActive(true);
    } else if (e.type === "dragleave") {
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) setDragActive(false);
    } else if (e.type === "dragover") {
      // dropEffect must be set so Chrome shows the "+ copy" cursor and accepts the drop
      try { e.dataTransfer.dropEffect = "copy"; } catch (_) {}
      setDragActive(true);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragActive(false);
    const dropped = e.dataTransfer?.files?.[0];
    if (dropped) handleFile(dropped);
  };

  const isAudioFile = (f) => estAudio(f.name);

  const handleFile = (selectedFile) => {
    setError("");
    // Tâche 1451 — la liste vient de `lib/formatsVideo.js`, c'est-à-dire la
    // MÊME que celle du serveur. Elle était plus courte ici : un .mkv ou un
    // .wmv était refusé avant l'envoi alors que le serveur les accepte.
    if (!extensionAcceptee(selectedFile.name)) {
      const motif = `Format non supporté. Acceptés : ${LISTE_MEDIA}`;
      setError(motif);
      // Ce refus a lieu DANS LE NAVIGATEUR : sans cet appel, il n'atteindrait
      // jamais le journal, qui manquerait le cas le plus fréquent.
      signalerRefusNavigateur([selectedFile], motif, "video");
      return;
    }
    if (selectedFile.size < 1000) {
      const motif = "Fichier trop petit : l'envoi a probablement été coupé.";
      setError(motif);
      signalerRefusNavigateur([selectedFile], motif, "video");
      return;
    }
    setFile(selectedFile);
    if (!title) setTitle(selectedFile.name.replace(/\.[^/.]+$/, ""));
  };

  // Vincent's bug 30-avril-2026: K8s/CloudFlare ingress on knoria.com cuts any
  // single multipart > ~100 Mo with a generic "Network Error". 7/7 fails on a
  // 240 Mo file, real videos are 700 Mo–1.2 Go. Solution: chunked upload above
  // a safe threshold. Below this size we keep the simple single-POST path.
  const CHUNK_THRESHOLD = 50 * 1024 * 1024;   // 50 Mo
  // Vincent 2026-07-21 (iter163 A+B+C+D+E) — Refonte upload chunked :
  // (B) Taille de chunk 5 Mo → 10 Mo : -50% de round-trips
  // (A) Concurrence 1 → 4 en parallèle : ~3x plus rapide sur les gros fichiers
  const CHUNK_SIZE = 10 * 1024 * 1024;         // 10 Mo per chunk (still under proxy limit)
  // Vincent B664 (Eric 2026-08-05 SPEC STRICK PROD) — Concurrence passée de
  // 4 à 6. Chrome/Firefox autorisent 6 connexions HTTP simultanées par
  // origine. Avec les DB writes désormais backgroundées (B664 backend), le
  // backend peut absorber les chunks à plein débit. Gain estimé sur une
  // connexion 30-100 Mbps : ~20-30% de vitesse d'upload en plus.
  const UPLOAD_CONCURRENCY = 6;                // parallel chunk uploads
  const MAX_CHUNK_RETRIES = 8;
  const CHUNK_BACKOFF_MAX_MS = 30_000;
  // (C) Reprise après crash : clé localStorage stable pour ce fichier
  const RESUME_STORAGE_KEY = "knoria_pending_upload_v1";

  // Identifie la cause profonde d'un échec chunk pour le logging serveur.
  const classifyChunkError = (err) => {
    if (!err) return { type: "unknown", detail: "no error object" };
    const status = err.response?.status;
    const code = err.code;
    // 1. Timeout côté axios
    if (code === "ECONNABORTED" || err.message?.toLowerCase().includes("timeout")) {
      return { type: "timeout", status, detail: `axios timeout après 120s` };
    }
    // 2. Erreur réseau (proxy coupe, connexion perdue, DNS, TLS)
    if (err.message?.includes("Network Error") || code === "ERR_NETWORK") {
      return { type: "network_error", status, detail: err.message || "Network Error" };
    }
    // 3. Server 5xx (proxy 502/503/504 typiques quand le worker back est saturé)
    if (typeof status === "number" && status >= 500) {
      return { type: "server_5xx", status, detail: `HTTP ${status} — ${(err.response?.data?.detail || "").toString().slice(0, 120)}` };
    }
    // 4. Client 4xx (session expirée, chunk_index invalide, upload_id inconnu)
    if (typeof status === "number" && status >= 400) {
      return { type: "client_4xx", status, detail: `HTTP ${status} — ${(err.response?.data?.detail || "").toString().slice(0, 120)}` };
    }
    // 5. Aucune réponse (proxy a fermé la connexion avant le retour serveur)
    if (!err.response && err.request) {
      return { type: "no_response", status: null, detail: "request sent, no response (proxy drop)" };
    }
    return { type: "unknown", status: status || null, detail: (err.message || "").slice(0, 200) };
  };

  const _humanError = (err, status, detail) => {
    if (typeof detail === "string" && detail.trim()) return `${detail} (HTTP ${status || "?"})`;
    if (Array.isArray(detail)) return `Validation: ${detail.map(d => d.msg).join(" · ")} (HTTP 422)`;
    if (status === 429) return "Trop d'uploads en peu de temps. Patiente 1 minute puis réessaie.";
    if (status === 413) return "Fichier trop volumineux pour le proxy. Découpe-le ou compresse-le (cible <100 Mo).";
    if (err?.message?.includes("Network Error")) {
      const sizeMb = file && file.size ? (file.size / (1024 * 1024)).toFixed(0) : null;
      return `Connexion interrompue pendant l'upload (fichier ${sizeMb || "?"} Mo). Le mode chunké a peut-être échoué ; réessayez.`;
    }
    return err?.message || t("error");
  };

  // Vincent 2026-07-21 (iter163) — État reprise + ETA
  const [resumeInfo, setResumeInfo] = useState(null); // { uploadId, filename, receivedCount, totalChunks }
  const [uploadEta, setUploadEta] = useState(null);   // "1min 30s remaining"

  // (E) Health check backend avant démarrage : détecte panne en 2s au lieu
  // d'échouer après plusieurs chunks.
  const _healthCheck = async () => {
    try {
      await axios.get(`${API}/api/health`, { withCredentials: true, timeout: 5000 });
      return true;
    } catch (e) {
      toast.error(t("error") + " — Le serveur ne répond pas. Vérifie ta connexion puis réessaie.");
      return false;
    }
  };

  // (C) Détecte au montage si une session upload est reprise-able.
  // Eric 2026-07-25 iter148 — Fix « React is not defined » : le hook utilisait
  // le préfixe namespace alors que React n'est PAS importé (seuls useState/useRef/
  // useEffect le sont). L'ErrorBoundary intercepte désormais ce genre d'erreur
  // au premier render → « Une erreur s'est produite ». Correction : utiliser
  // le useEffect déjà importé nommément à la ligne 1.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RESUME_STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved?.upload_id || !saved?.filename) return;
      // On vérifie côté serveur si la session existe encore
      axios.get(`${API}/api/videos/upload/${saved.upload_id}/status`, {
        withCredentials: true, timeout: 8000,
      }).then(({ data }) => {
        if (data.is_complete) {
          localStorage.removeItem(RESUME_STORAGE_KEY);
          return;
        }
        setResumeInfo({
          uploadId: data.upload_id,
          filename: data.filename,
          receivedCount: data.received_count,
          totalChunks: data.total_chunks,
          savedContext: saved,
        });
      }).catch(() => {
        // Session expirée côté serveur : cleanup local
        localStorage.removeItem(RESUME_STORAGE_KEY);
      });
    } catch (_) { /* corrupted localStorage entry — ignore */ }
  }, []);

  const uploadChunked = async (resumeFromId = null) => {
    // (E) Health check
    if (!(await _healthCheck())) throw new Error("Server unreachable");

    // Vincent B666 (Eric 2026-08-05 SPEC STRICK PROD) — Signaler l'upload
    // actif pour pauser tous les pollers globaux (notifications, version,
    // agora, pending, drafts). Sinon ils saturent l'origine et TOUT
    // retourne 520 pendant l'upload.
    const uploadTag = `chunked-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    uploadState.start(uploadTag);
    try {
      return await _uploadChunkedInner(resumeFromId);
    } finally {
      uploadState.end(uploadTag);
    }
  };

  const _uploadChunkedInner = async (resumeFromId = null) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let uploadId;
    let alreadyReceived = new Set();
    // Vincent bogue 666 (Eric 2026-08-05) — Rapport à retourner en même
    // temps que doneData pour l'affichage post-upload.
    const startedAt = Date.now();
    let initMs = 0;
    let completeMs = 0;

    if (resumeFromId) {
      // (C) REPRISE : on récupère l'état côté serveur et on ne renvoie que les chunks manquants.
      const { data: status } = await axios.get(
        `${API}/api/videos/upload/${resumeFromId}/status`,
        { withCredentials: true, timeout: 8000 },
      );
      uploadId = status.upload_id;
      alreadyReceived = new Set(status.received_chunks || []);
      toast.info(`Reprise de l'upload : ${alreadyReceived.size}/${totalChunks} morceaux déjà envoyés.`);
    } else {
      // 1) INIT
      const initParams = new URLSearchParams({
        title, description, training_theme: theme,
        filename: file.name, total_size: String(file.size),
        total_chunks: String(totalChunks),
      });
      if (diarizationMode === "single" && singleSpeakerId) {
        initParams.append("skip_diarization", "true");
        initParams.append("assigned_speaker_id", singleSpeakerId);
      }
      if (diarizationMode === "multi_select" && multiSelectedSpeakerIds.size > 0) {
        initParams.append("assigned_speaker_ids", Array.from(multiSelectedSpeakerIds).join(","));
      }
      const initStart = Date.now();
      const { data: initData } = await axios.post(
        `${API}/api/videos/upload/init?${initParams.toString()}`,
        null, { withCredentials: true },
      );
      initMs = Date.now() - initStart;
      uploadId = initData.upload_id;
      // (C) Persist immédiatement pour survivre à un crash / refresh
      try {
        localStorage.setItem(RESUME_STORAGE_KEY, JSON.stringify({
          upload_id: uploadId,
          filename: file.name,
          total_size: file.size,
          title, description, theme,
          created_at: new Date().toISOString(),
        }));
      } catch (_) { /* localStorage plein — ignore */ }
    }

    // 2) UPLOAD PARALLÈLE avec worker pool (concurrence UPLOAD_CONCURRENCY)
    const chunksToUpload = [];
    for (let i = 0; i < totalChunks; i++) {
      if (!alreadyReceived.has(i)) chunksToUpload.push(i);
    }
    let completedChunks = alreadyReceived.size;
    const chunkFailureLog = [];
    const uploadStartTime = Date.now();

    // Eric 2026-08-05 (alerte vidéos lentes) — Si l'upload dépasse 10 min,
    // notifie une seule fois les admins de l'org via le backend pour qu'ils
    // interviennent AVANT que l'utilisateur ne rapporte l'incident. Le
    // timer est annulé si l'upload termine avant (voir `finally` en fin de
    // fonction). Idempotent côté backend (filtre par upload_id).
    const SLOW_UPLOAD_THRESHOLD_MS = 10 * 60 * 1000;
    let slowWarned = false;
    const slowTimer = setTimeout(() => {
      if (slowWarned) return;
      slowWarned = true;
      axios.post(
        `${API}/api/videos/upload/${uploadId}/slow-warning`,
        { elapsed_ms: Date.now() - uploadStartTime },
        { withCredentials: true, timeout: 10000 },
      ).catch((slowErr) => {
        // Best-effort : un échec ici n'affecte pas l'upload en cours.
        console.warn("Slow-upload alert failed (non-fatal):", slowErr);
      });
    }, SLOW_UPLOAD_THRESHOLD_MS);

    const uploadOneChunk = async (i) => {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      let blob = file.slice(start, end);
      let fd = new FormData();
      fd.append("chunk", blob, `${file.name}.part${i}`);
      let attempt = 0;
      let lastErr = null;
      while (attempt < MAX_CHUNK_RETRIES) {
        try {
          await axios.post(
            `${API}/api/videos/upload/chunk?upload_id=${uploadId}&chunk_index=${i}`,
            fd,
            { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 },
          );
          blob = null; fd = null;
          return; // success
        } catch (chunkErr) {
          attempt += 1;
          lastErr = chunkErr;
          const cause = classifyChunkError(chunkErr);
          chunkFailureLog.push({ chunk_index: i, attempt, type: cause.type, http_status: cause.status, detail: cause.detail, at: new Date().toISOString() });
          if (attempt >= MAX_CHUNK_RETRIES) {
            blob = null; fd = null;
            throw chunkErr;
          }
          const base = Math.min(1000 * Math.pow(2, attempt - 1), CHUNK_BACKOFF_MAX_MS);
          const jitter = Math.floor(Math.random() * Math.min(1000, base * 0.3));
          const waitMs = base + jitter;
          try { toast.warning(`Morceau ${i + 1}/${totalChunks} — retry ${attempt}/${MAX_CHUNK_RETRIES}`, { duration: Math.min(waitMs, 4000) }); } catch (_) { /* toast optional */ }
          await new Promise((r) => setTimeout(r, waitMs));
        }
      }
      void lastErr;
    };

    // Worker pool : lance UPLOAD_CONCURRENCY tâches en parallèle. Chaque
    // worker consomme la prochaine tâche libre jusqu'à épuisement.
    let nextIdx = 0;
    const worker = async () => {
      while (true) {
        const myIdx = nextIdx++;
        if (myIdx >= chunksToUpload.length) return;
        const chunkNum = chunksToUpload[myIdx];
        await uploadOneChunk(chunkNum);
        completedChunks += 1;
        const pct = Math.round((completedChunks * 100) / totalChunks);
        setUploadProgress(pct);
        // (D) ETA calculation : basé sur la vitesse observée depuis le début
        const elapsed = (Date.now() - uploadStartTime) / 1000;
        const chunksProcessedThisRun = completedChunks - alreadyReceived.size;
        if (chunksProcessedThisRun > 0 && elapsed > 3) {
          const chunksPerSec = chunksProcessedThisRun / elapsed;
          const remaining = totalChunks - completedChunks;
          const etaSec = Math.round(remaining / Math.max(chunksPerSec, 0.001));
          if (etaSec > 60) setUploadEta(`~${Math.floor(etaSec / 60)}min ${etaSec % 60}s restant`);
          else if (etaSec > 0) setUploadEta(`~${etaSec}s restant`);
          else setUploadEta(null);
        }
      }
    };
    try {
      await Promise.all(Array(UPLOAD_CONCURRENCY).fill(0).map(() => worker()));
    } catch (chunkErr) {
      // Un des workers a abandonné après 8 retries : rapport puis cleanup optionnel
      try {
        await axios.post(
          `${API}/api/videos/upload/${uploadId}/chunk-failure-report`,
          { failures: chunkFailureLog, gave_up_on_chunk: nextIdx - 1, total_chunks: totalChunks, file_size: file.size },
          { withCredentials: true, timeout: 10000 },
        );
      } catch (_) { /* best-effort */ }
      // On NE supprime PAS la session : (C) permet de reprendre après crash
      throw chunkErr;
    }

    if (chunkFailureLog.length > 0) {
      try {
        await axios.post(
          `${API}/api/videos/upload/${uploadId}/chunk-failure-report`,
          { failures: chunkFailureLog, gave_up_on_chunk: null, total_chunks: totalChunks, file_size: file.size, recovered: true },
          { withCredentials: true, timeout: 10000 },
        );
      } catch (_) { /* best-effort */ }
    }
    setCurrentStep(1);
    setUploadEta(null);

    // 3) COMPLETE
    let doneData;
    const completeStart = Date.now();
    if (firefliesFile) {
      const doneFd = new FormData();
      doneFd.append("fireflies_file", firefliesFile);
      const resp = await axios.post(
        `${API}/api/videos/upload/complete?upload_id=${uploadId}`,
        doneFd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 },
      );
      doneData = resp.data;
    } else {
      const resp = await axios.post(
        `${API}/api/videos/upload/complete?upload_id=${uploadId}`,
        null,
        { withCredentials: true, timeout: 600000 },
      );
      doneData = resp.data;
    }
    completeMs = Date.now() - completeStart;
    // Eric 2026-08-05 (alerte vidéos lentes) — Upload terminé, on annule le
    // timer d'alerte 10 min (n'a plus lieu d'être).
    clearTimeout(slowTimer);
    // (C) Success : cleanup localStorage
    try { localStorage.removeItem(RESUME_STORAGE_KEY); } catch (_) { /* ignore */ }
    setResumeInfo(null);

    // Vincent bogue 666 (Eric 2026-08-05) — Compilation du rapport d'upload
    // à afficher sur l'écran de succès. Regroupe les difficultés par type
    // pour une lecture rapide (ex: "3 morceaux ont dû être renvoyés").
    const totalMs = Date.now() - startedAt;
    const chunkMs = totalMs - initMs - completeMs;
    const failuresByType = {};
    for (const f of chunkFailureLog) {
      const key = f.http_status ? `HTTP ${f.http_status}` : (f.type || "erreur réseau");
      failuresByType[key] = (failuresByType[key] || 0) + 1;
    }
    const chunksWithRetries = new Set(chunkFailureLog.map((f) => f.chunk_index)).size;
    doneData._uploadReport = {
      mode: "chunked",
      total_ms: totalMs,
      init_ms: initMs,
      chunks_ms: Math.max(0, chunkMs),
      complete_ms: completeMs,
      total_chunks: totalChunks,
      already_received: alreadyReceived.size,
      resumed: !!resumeFromId,
      file_size: file.size,
      concurrency: UPLOAD_CONCURRENCY,
      chunks_with_retries: chunksWithRetries,
      total_retries: chunkFailureLog.length,
      failures_by_type: failuresByType,
      // Vincent B837 (Eric 2026-02-14 SPEC STRICK PROD) — Traçabilité :
      // le rapport post-upload doit préciser noms de fichiers + IDs pour
      // faciliter les recherches ultérieures (support, audit Loi 25).
      video_filename: file.name,
      video_id: doneData?.id || null,
      fireflies_filename: firefliesFile?.name || null,
      fireflies_id: doneData?.fireflies_id || doneData?.fireflies_transcript_id || null,
    };
    return doneData;
  };

  const uploadSingle = async () => {
    const startedAt = Date.now();
    const formData = new FormData();
    formData.append("file", file);
    // Vincent #406 (Eric 2026-07-02 SPEC STRICK) — Passer les paramètres de
    // diarisation si "1 seul locuteur connu" est sélectionné.
    const extraParams = [];
    if (diarizationMode === "single" && singleSpeakerId) {
      extraParams.push(`skip_diarization=true`);
      extraParams.push(`assigned_speaker_id=${encodeURIComponent(singleSpeakerId)}`);
    }
    // Vincent XLSX 2026-07-16 Row 499 (Eric 2026-07-16 SPEC STRICK) —
    // Mode multi_select : envoie assigned_speaker_ids en query param.
    if (diarizationMode === "multi_select" && multiSelectedSpeakerIds.size > 0) {
      extraParams.push(`assigned_speaker_ids=${encodeURIComponent(Array.from(multiSelectedSpeakerIds).join(","))}`);
    }
    // Vincent #417 (v2 2026-07-06 21h50) — Fichier Fireflies OPTIONNEL,
    // envoyé dans le même multipart. Backend le stocke pour utilisation
    // à l'étape de fusion (n'affecte PAS la transcription Whisper).
    if (firefliesFile) {
      formData.append("fireflies_file", firefliesFile);
    }
    const extraQuery = extraParams.length > 0 ? "&" + extraParams.join("&") : "";
    const { data } = await axios.post(
      `${API}/api/videos/upload?title=${encodeURIComponent(title)}&description=${encodeURIComponent(description)}&training_theme=${theme}${extraQuery}`,
      formData,
      {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
          if (progress >= 100) setCurrentStep(1);
        }
      }
    );
    // Vincent bogue 666 (Eric 2026-08-05) — Rapport upload direct (fichier
    // < seuil chunké), pas de retries possibles côté client.
    data._uploadReport = {
      mode: "direct",
      total_ms: Date.now() - startedAt,
      file_size: file.size,
      total_chunks: 1,
      chunks_with_retries: 0,
      total_retries: 0,
      failures_by_type: {},
      // Vincent B837 (Eric 2026-02-14 SPEC STRICK PROD) — voir upload chunké.
      video_filename: file.name,
      video_id: data?.id || null,
      fireflies_filename: firefliesFile?.name || null,
      fireflies_id: data?.fireflies_id || data?.fireflies_transcript_id || null,
    };
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError(t("select_file")); return; }
    if (!title.trim()) { setError(t("enter_title")); return; }
    // Eric 2026-07-07 URGENCE PROD FINALE — Si "1 seul locuteur connu" mode
    // est sélectionné, l'utilisateur DOIT choisir un locuteur dans la liste.
    // Sinon le param `skip_diarization` n'est pas envoyé au backend et la
    // vidéo tombe dans la branche AssemblyAI classique — ce que l'utilisateur
    // voulait justement éviter.
    if (diarizationMode === "single" && !singleSpeakerId) {
      setError(lang === "fr"
        ? "Veuillez sélectionner un locuteur connu dans la liste, ou choisissez 'identifier automatiquement les locuteurs' pour laisser l'IA le faire."
        : "Please select a known speaker from the list, or choose 'identify speakers automatically' to let AI do it.");
      return;
    }

    // Tâche 1183 (Eric 2026-06-25) — Whisper contourné sans transcription
    // Fireflies : aucun texte ne serait produit. On refuse AVANT l'envoi, en
    // nommant les deux seules sorties possibles.
    if (skipWhisper && !firefliesFile) {
      setError(lang === "fr"
        ? "Le kill switch « Whisper » est à OFF : la transcription est contournée. Joignez la transcription Fireflies de cette rencontre, ou remettez « Whisper » à ON ci-dessus."
        : "The « Whisper » kill switch is OFF: transcription is bypassed. Attach the Fireflies transcript, or switch « Whisper » back ON above.");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setCurrentStep(0);

    try {
      // Eric 2026-08-05 (direct upload) — Nouveau flow browser→S3 direct
      // si l'org est éligible ; sinon fallback à l'ancien flow chunké /
      // single. Le dispatch se fait UNIQUEMENT ici pour ne toucher au reste
      // du composant qu'au minimum.
      let data;
      if (directUploadEnabled) {
        uploadState.start("direct-s3");
        try {
          data = await uploadDirect(file, {
            API,
            title, description, theme,
            firefliesFile,
            diarizationMode,
            singleSpeakerId,
            multiSelectedSpeakerIds,
            concurrency: 3,
          }, {
            onProgress: ({ completed, total }) => {
              const pct = Math.min(99, Math.round((completed / total) * 100));
              setUploadProgress(pct);
            },
            onStage: (stage) => {
              // Mappe les stages direct → currentStep pour l'UI
              if (stage === "init") setCurrentStep(0);
              else if (stage === "upload") setCurrentStep(0);
              else if (stage === "complete") setCurrentStep(1);
            },
            onSlowUpload: () => {
              toast.warning(lang === "fr"
                ? "L'envoi prend plus de 10 minutes. Les admins ont été alertés."
                : "Upload is taking more than 10 minutes. Admins have been notified.");
            },
          });
          setUploadProgress(100);
        } finally {
          uploadState.end("direct-s3");
        }
      } else {
        data = file.size > CHUNK_THRESHOLD
          ? await uploadChunked(resumeInfo?.uploadId || null)
          : await uploadSingle();
      }
      setVideoId(data.id);
      setCurrentStep(2);

      // Vincent B798 (Eric 2026-02-12) — Le backend signale via
      // `fireflies_status` si le fichier Fireflies a été joint mais
      // n'a pas pu être importé (upload S3 client silencieux ou
      // ingestion backend échouée). Sans ce toast, l'échec Fireflies
      // était 100% silencieux et Vincent voyait « Fireflies (non
      // importée) » dans la page vidéo alors qu'il pensait l'avoir
      // fourni.
      if (data?.fireflies_status && data.fireflies_status !== "ok") {
        toast.warning(
          lang === "fr"
            ? "Fireflies non importé"
            : "Fireflies not imported",
          {
            description: data.fireflies_warning || (lang === "fr"
              ? "Le fichier Fireflies n'a pas pu être joint à cette vidéo. Utilise « Importer transcription » dans la page vidéo pour ré-essayer."
              : "The Fireflies file could not be attached to this video. Use \"Import transcript\" in the video page to retry."),
            duration: 12000,
          }
        );
      }
      // Vincent bogue 666 (Eric 2026-08-05) — Rapport d'upload à afficher
      // sur l'écran de succès. Extrait de la clé injectée par uploadChunked,
      // uploadSingle OU uploadDirect. On l'extrait AVANT `setSuccess(true)`
      // pour qu'il soit dispo au premier rendu de l'écran de succès.
      if (data && data._uploadReport) {
        setUploadReport(data._uploadReport);
        // Eric 2026-08-05 (bogue 666 — rapport dans la fiche vidéo) —
        // Persiste le rapport sur le document vidéo pour qu'il soit
        // consultable depuis la page de détail après coup. Fire-and-forget :
        // un échec n'affecte pas le succès de l'upload (le rapport local
        // continue de s'afficher sur l'écran actuel).
        if (data.id) {
          axios.patch(
            `${API}/api/videos/${data.id}/upload-report`,
            data._uploadReport,
            { withCredentials: true, timeout: 10000 },
          ).catch((rptErr) => {
            console.warn("Upload report persistence failed (non-fatal):", rptErr);
          });
        }
      }
      // Vincent #263 (2026-06-11) — Rattacher la vidéo à TOUS les parcours
      // cochés. L'erreur n'empêche pas le succès de l'upload (la vidéo existe
      // déjà, juste pas attachée — Vincent peut le faire manuellement).
      if (selectedPathwayIds.length > 0 && data?.id) {
        for (const pid of selectedPathwayIds) {
          try {
            await axios.post(
              `${API}/api/pathways/${pid}/add-video`,
              { video_id: data.id },
              { withCredentials: true }
            );
          } catch (pwErr) {
            console.warn("Pathway attach failed:", pid, pwErr);
          }
        }
      }
      setSuccess(true);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      const msg = _humanError(err, status, detail);
      console.error("[Upload]", err);
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const formatSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " Ko";
    return (bytes / (1024 * 1024)).toFixed(1) + " Mo";
  };

  // Vincent 2026-07-07 SPEC STRICK (crash 2e upload) — reset complet du
  // state après un upload. Sans ce reset, le File blob de la 1re vidéo
  // (potentiellement 500 Mo à 1 Go) restait pinné en mémoire du navigateur,
  // et sélectionner un 2e fichier faisait exploser Chrome/Safari/Firefox.
  const resetForUpload = () => {
    setFile(null);
    setTitle("");
    setDescription("");
    setTheme("general");
    setUploadProgress(0);
    setCurrentStep(-1);
    setSuccess(false);
    setVideoId(null);
    setError("");
    setUploadReport(null);
    setSelectedPathwayIds([]);
    setDiarizationMode("multi");
    setSingleSpeakerId("");
    setFirefliesFile(null);
    if (firefliesInputRef.current) firefliesInputRef.current.value = "";
    // Yield 100ms au GC pour libérer les blobs avant que l'utilisateur ne
    // sélectionne un nouveau fichier.
    setTimeout(() => {
      try { if (window.gc) window.gc(); } catch (_) { /* not exposed in prod builds */ }
    }, 100);
  };

  if (success) {
    return (
      <div className="max-w-xl mx-auto py-12 text-center animate-fade-in" data-testid="upload-success">
        <div className="w-16 h-16 bg-teal-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-teal-500/20">
          <CheckCircle size={32} className="text-teal-400" weight="fill" />
        </div>
        <h2 className="text-2xl font-bold font-['Outfit']">{t("file_received")}</h2>
        <p className="text-muted-foreground mt-2 mb-1">{t("pipeline_processing")}</p>
        <p className="text-xs text-muted-foreground mb-6">{t("pipeline_timing")}</p>

        {/* Tâche 1313 (Eric 2026-06-30) — Alerte « enregistrement saturé »,
            juste sous la confirmation d'envoi : c'est le seul endroit où
            l'importateur regarde encore, et c'est le moment où il peut
            décider de refaire la prise avant que le traitement n'avance. */}
        <AudioSaturationAlert videoId={videoId} />

        {/* Vincent bogue 666 (Eric 2026-08-05) — Rapport d'upload : durée
            totale, débit moyen et difficultés rencontrées. Affiché SOUS
            "Fichier envoyé !" pour donner à l'utilisateur (Vincent) une vue
            claire sur ce qui s'est passé pendant l'envoi (utile pour repérer
            un upload lent ou des retries silencieux). */}
        {uploadReport && (() => {
          const totalSec = Math.max(0, Math.round(uploadReport.total_ms / 1000));
          const mins = Math.floor(totalSec / 60);
          const secs = totalSec % 60;
          const durationStr = mins > 0 ? `${mins} min ${secs} s` : `${secs} s`;
          const mo = uploadReport.file_size / (1024 * 1024);
          const throughput = totalSec > 0 ? (mo / totalSec).toFixed(1) : "—";
          const hasFailures = uploadReport.total_retries > 0;
          const failureLines = Object.entries(uploadReport.failures_by_type || {})
            .sort((a, b) => b[1] - a[1])
            .map(([k, v]) => `${v} × ${k}`);
          return (
            <div
              className={`rounded-xl border p-4 mb-6 text-left text-sm ${hasFailures
                ? "bg-amber-500/5 border-amber-500/30"
                : "bg-teal-500/5 border-teal-500/20"}`}
              data-testid="upload-report"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
                  {lang === "fr" ? "Rapport d'envoi" : "Upload report"}
                </p>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${hasFailures
                  ? "bg-amber-500/15 text-amber-700 dark:text-amber-300"
                  : "bg-teal-500/15 text-teal-700 dark:text-teal-300"}`}>
                  {hasFailures
                    ? (lang === "fr" ? "Difficultés rencontrées" : "Issues encountered")
                    : (lang === "fr" ? "Sans encombre" : "Smooth")}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Durée totale" : "Total duration"}
                  </span>
                  <span className="font-semibold" data-testid="upload-report-duration">{durationStr}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Débit moyen" : "Average throughput"}
                  </span>
                  <span className="font-semibold" data-testid="upload-report-throughput">{throughput} Mo/s</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Taille du fichier" : "File size"}
                  </span>
                  <span className="font-semibold">{formatSize(uploadReport.file_size)}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {lang === "fr" ? "Mode d'envoi" : "Upload mode"}
                  </span>
                  <span className="font-semibold">
                    {uploadReport.mode === "direct_s3"
                      ? (lang === "fr"
                        ? `Direct S3 — ${uploadReport.total_chunks} × ${Math.round((uploadReport.file_size / uploadReport.total_chunks) / (1024 * 1024))} Mo`
                        : `Direct S3 — ${uploadReport.total_chunks} × ${Math.round((uploadReport.file_size / uploadReport.total_chunks) / (1024 * 1024))} Mo`)
                      : uploadReport.mode === "chunked"
                      ? (lang === "fr"
                        ? `Découpé — ${uploadReport.total_chunks} × 10 Mo${uploadReport.resumed ? " (repris)" : ""}`
                        : `Chunked — ${uploadReport.total_chunks} × 10 Mo${uploadReport.resumed ? " (resumed)" : ""}`)
                      : (lang === "fr" ? "Direct (un seul envoi)" : "Direct (single upload)")}
                  </span>
                </div>
              </div>
              {/* Vincent B837 (Eric 2026-02-14 SPEC STRICK PROD) —
                  Bloc de traçabilité : nom + ID de la vidéo et du fichier
                  Fireflies (si joint). Aide au support/audit. */}
              {(uploadReport.video_filename || uploadReport.video_id || uploadReport.fireflies_filename || uploadReport.fireflies_id) && (
                <div
                  className="mt-3 pt-3 border-t border-border/50 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5"
                  data-testid="upload-report-traceability"
                >
                  {uploadReport.video_filename && (
                    <div className="flex flex-col" data-testid="upload-report-video-filename">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {lang === "fr" ? "Nom du fichier vidéo" : "Video filename"}
                      </span>
                      <span className="font-mono text-xs break-all">{uploadReport.video_filename}</span>
                    </div>
                  )}
                  {uploadReport.video_id && (
                    <div className="flex flex-col" data-testid="upload-report-video-id">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {lang === "fr" ? "ID vidéo" : "Video ID"}
                      </span>
                      <span className="font-mono text-xs break-all">{uploadReport.video_id}</span>
                    </div>
                  )}
                  {uploadReport.fireflies_filename && (
                    <div className="flex flex-col" data-testid="upload-report-fireflies-filename">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {lang === "fr" ? "Nom du fichier Fireflies" : "Fireflies filename"}
                      </span>
                      <span className="font-mono text-xs break-all">{uploadReport.fireflies_filename}</span>
                    </div>
                  )}
                  {uploadReport.fireflies_id && (
                    <div className="flex flex-col" data-testid="upload-report-fireflies-id">
                      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                        {lang === "fr" ? "ID Fireflies" : "Fireflies ID"}
                      </span>
                      <span className="font-mono text-xs break-all">{uploadReport.fireflies_id}</span>
                    </div>
                  )}
                </div>
              )}
              {(uploadReport.mode === "chunked" || uploadReport.mode === "direct_s3") && (
                <div className="mt-3 pt-3 border-t border-border/50">
                  {hasFailures ? (
                    <>
                      <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1.5" data-testid="upload-report-difficulties-title">
                        {lang === "fr"
                          ? `${uploadReport.chunks_with_retries} morceau(x) sur ${uploadReport.total_chunks} ont dû être renvoyés (${uploadReport.total_retries} tentative(s) au total).`
                          : `${uploadReport.chunks_with_retries} of ${uploadReport.total_chunks} chunks were retried (${uploadReport.total_retries} attempts in total).`}
                      </p>
                      <ul className="text-xs text-muted-foreground list-disc pl-5 space-y-0.5" data-testid="upload-report-difficulties-list">
                        {failureLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                      <p className="text-[10px] text-muted-foreground mt-2 italic">
                        {lang === "fr"
                          ? "Les retries automatiques ont permis de récupérer sans intervention. La vidéo est intacte."
                          : "Automatic retries recovered without intervention. The video is intact."}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground" data-testid="upload-report-no-difficulties">
                      {lang === "fr"
                        ? `Aucune difficulté : les ${uploadReport.total_chunks} morceaux sont passés du premier coup${uploadReport.already_received > 0 ? ` (dont ${uploadReport.already_received} déjà envoyé(s) via reprise)` : ""}.`
                        : `No issues: all ${uploadReport.total_chunks} chunks succeeded on first try${uploadReport.already_received > 0 ? ` (${uploadReport.already_received} already sent via resume)` : ""}.`}
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Live pipeline tracker */}
        <div className="bg-card border border-border rounded-xl p-5 text-left mb-6" data-testid="pipeline-tracker">
          <p className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-4">{t("pipeline_processing")}</p>
          <div className="space-y-3">
            {PIPELINE_STEPS.map((step, i) => {
              const Icon = step.icon;
              const isDone = i <= currentStep;
              const isActive = i === currentStep + 1;
              return (
                <div key={step.key} className={`flex items-center gap-3 p-2.5 rounded-lg transition-all ${isDone ? "bg-teal-500/5" : isActive ? "bg-cyan-500/5" : ""}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${isDone ? "bg-teal-500/15" : isActive ? "bg-cyan-500/15" : "bg-secondary"}`}>
                    {isDone ? <CheckCircle size={14} weight="fill" className="text-teal-400" /> : isActive ? <SpinnerGap size={14} className="text-cyan-400 animate-spin" /> : <Icon size={14} className="text-muted-foreground" />}
                  </div>
                  <span className={`text-sm ${isDone ? "text-teal-400 font-medium" : isActive ? "text-cyan-400" : "text-muted-foreground"}`}>{t(step.key)}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Vincent 2026-07-07 SPEC STRICK — Bouton dédié pour uploader une
            autre vidéo. Reset propre du state → libère la mémoire du File
            blob précédent avant que l'utilisateur ne pique un 2e gros
            fichier. Prévient les crashs Chrome/Safari/Firefox. */}
        <div className="flex gap-3 flex-wrap justify-center">
          <Button onClick={resetForUpload} className="gap-2 bg-teal-600 hover:bg-teal-500" data-testid="upload-another-btn">
            <CloudArrowUp size={16} /> {lang === "fr" ? "Uploader une autre vidéo" : "Upload another video"}
          </Button>
          <Button onClick={() => navigate("/dashboard")} variant="outline" className="gap-2" data-testid="go-dashboard-btn">
            {t("back_to_dashboard")} <ArrowRight size={16} />
          </Button>
          {videoId && (
            <Button variant="outline" onClick={() => navigate(`/dashboard/video/${videoId}`)} data-testid="view-video-btn">
              {t("view_video")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in" data-testid="upload-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight font-['Outfit']">{t("upload_title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("upload_subtitle")}</p>
      </div>

      {/* Tâche 1451 (Eric 2026-06) — « Affiche aussi la section qui liste les
          fichiers refusés dans la section d'import associée ». C'est ici qu'on
          se trouve quand un import vient d'échouer : aller chercher la cause
          dans l'Explorateur de fichiers suppose de savoir qu'elle y est. */}
      <ImportRefusalsPanel kind="video" />

      {/* Quota banner — shown when a finite limit exists */}
      {usage && usage.metrics && usage.metrics.videos_uploaded && !usage.founder_exempt && usage.metrics.videos_uploaded.limit > 0 && (() => {
        const m = usage.metrics.videos_uploaded;
        const pct = m.percent || 0;
        const blocked = !m.allowed;
        const warn = pct >= 80 && !blocked;
        const tone = blocked
          ? "bg-red-50 border-red-200 text-red-900"
          : warn
          ? "bg-amber-50 border-amber-200 text-amber-900"
          : "bg-teal-50 border-teal-200 text-teal-900";
        const bar = blocked ? "bg-red-500" : warn ? "bg-amber-500" : "bg-teal-500";
        return (
          <div className={`mb-4 p-3 border rounded-lg text-xs ${tone}`} data-testid="upload-quota-banner">
            <div className="flex items-center justify-between mb-1.5">
              <p className="font-semibold">
                Quota vidéos ce mois ({usage.plan_name || usage.plan})
              </p>
              <span className="font-mono font-semibold">{m.current} / {m.limit}</span>
            </div>
            <div className="w-full h-2 bg-white/60 rounded overflow-hidden">
              <div className={`h-full ${bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
            </div>
            {blocked && (
              <p className="mt-2 text-[11px]">
                {tr("Limite atteinte.", "Limit reached.")} <a href="/dashboard/billing" className="underline font-semibold" data-testid="upload-quota-upgrade-link">{tr("Passer à un plan supérieur", "Move up to a higher plan")}</a> {tr("pour importer davantage ce mois-ci.", "to import more this month.")}
              </p>
            )}
          </div>
        );
      })()}

      {/* Constraints helper — directly answers Vincent's question */}
      <div className="mb-5 p-3 bg-blue-50/50 border border-blue-200 rounded-lg text-xs text-blue-900" data-testid="upload-constraints">
        {/* Tâche 1377 (Eric 2026-06-30) — Ce bloc restait français sous un
            titre anglais. */}
        <p className="font-semibold mb-1">{tr("Contraintes d'envoi", "Upload limits")}</p>
        <ul className="space-y-0.5 list-disc list-inside text-blue-800/90">
          <li><strong>{tr("Formats vidéo", "Video formats")}</strong> : MP4 {tr("(recommandé)", "(recommended)")}, MOV, AVI, WebM, MKV, WMV, FLV, M4V, TS, MPG, MPEG, 3GP</li>
          <li><strong>{tr("Formats audio", "Audio formats")}</strong> : MP3, WAV, M4A, AAC, OGG, FLAC, WMA</li>
          <li><strong>{tr("Taille max", "Max size")}</strong> : <strong>{tr("jusqu'à 5 Go", "up to 5 GB")}</strong>. {tr(
            "Au-delà de 50 Mo, l'envoi bascule automatiquement en mode découpé (morceaux de 5 Mo) pour éviter les coupures du proxy. Réessais automatiques en cas de perte de connexion.",
            "Past 50 MB the upload automatically switches to chunked mode (5 MB chunks) to avoid proxy cut-offs. Automatic retries if the connection drops.",
          )}</li>
          <li><strong>{tr("Nom de fichier", "File name")}</strong> : {tr(
            "pas de contrainte stricte. Évite les caractères spéciaux exotiques (œ, é dans le nom passent, mais préfère l'ASCII pour le stockage).",
            "no strict constraint. Avoid exotic special characters (œ, é in the name are fine, but prefer ASCII for storage).",
          )}</li>
          <li><strong>{tr("Limite de débit", "Rate limit")}</strong> : {tr(
            "30 envois / min / utilisateur (largement suffisant).",
            "30 uploads / min / user (more than enough).",
          )}</li>
        </ul>
      </div>

      {/* Vincent #422 + #425 (Eric 2026-07-06→07 SPEC STRICK) — Kill-switches
          accessibles aux concepteurs, admins et founders (pas seulement founder). */}
      {canEditVideos && (
        <div className="mb-5 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-center gap-3 flex-wrap" data-testid="upload-kill-switches">
          <span className="text-xs font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">{tr("Interrupteurs transcription :", "Kill switch transcription:")}</span>
          {/* Tâche 1183 (Eric 2026-06-25) — Whisper en PREMIER, à ON par
              défaut. À OFF, la transcription Whisper est entièrement
              contournée au profit de la transcription Fireflies fournie. */}
          <button
            type="button"
            onClick={toggleSkipWhisper}
            disabled={uploading || flagsUnknown}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md ${skipWhisper ? "bg-amber-500 text-white" : "bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border border-amber-300"} ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="upload-toggle-skip-whisper-btn"
            title={uploading
              ? tr("Verrouillé pendant l'analyse Whisper", "Locked while Whisper is running")
              : (skipWhisper
                  ? "Transcription Whisper CONTOURNÉE — le pipeline utilise la transcription Fireflies jointe (obligatoire). Cliquer pour réactiver Whisper."
                  : "Contourner complètement la transcription Whisper (pour les organisations qui fournissent leur transcription Fireflies)")}
          >
            Whisper : {skipWhisper === null ? "…" : etatSwitch(skipWhisper)}
          </button>
          <button
            type="button"
            onClick={toggleSkipDiar}
            disabled={uploading || flagsUnknown || skipWhisper}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md ${skipDiar ? "bg-amber-500 text-white" : "bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border border-amber-300"} ${(uploading || skipWhisper) ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="upload-toggle-skip-diar-btn"
            title={uploading ? tr("Verrouillé pendant l'analyse Whisper", "Locked while Whisper is running") : (skipWhisper ? tr("Inactif : Whisper est à OFF", "Inactive: Whisper is OFF") : (skipDiar ? tr("AssemblyAI diarization DÉSACTIVÉE — cliquer pour réactiver", "AssemblyAI diarization DISABLED — click to re-enable") : tr("Désactiver AssemblyAI diarization (utile quand la file hang à 55%)", "Disable AssemblyAI diarization (useful when the queue hangs at 55%)")))}
          >
            {tr("Diarisation : ", "Diarization: ")}{skipDiar === null ? "…" : etatSwitch(skipDiar)}
          </button>
          <button
            type="button"
            onClick={toggleSkipVisual}
            disabled={uploading || flagsUnknown || skipWhisper || (skipVisual && skipDiar)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md ${skipVisual ? "bg-amber-500 text-white" : "bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-200 border border-amber-300"} ${(uploading || skipWhisper || (skipVisual && skipDiar)) ? "opacity-50 cursor-not-allowed" : ""}`}
            data-testid="upload-toggle-skip-visual-btn"
            title={uploading ? tr("Verrouillé pendant l'analyse Whisper", "Locked while Whisper is running") : ((skipVisual && skipDiar) ? tr("Réactivez d'abord la Diarisation pour pouvoir réactiver l'Identification faciale", "Re-enable Diarization first, then you can re-enable Facial ID") : (skipVisual ? tr("Identification faciale du locuteur DÉSACTIVÉE — cliquer pour réactiver", "Speaker facial ID DISABLED — click to re-enable") : "Désactiver l'identification faciale du locuteur (à partir de sa photo, via GPT Vision). Utile quand la file d'attente hang au stade Vision."))}
          >
            {tr("ID faciale : ", "Facial ID: ")}{skipVisual === null ? "…" : etatSwitch(skipVisual)}
          </button>
          {/* Vincent B570 (Eric 2026-07-27) — Message d'info sur la
              dépendance Diarisation → Identification faciale + verrouillage
              pendant analyse. */}
          <span className="text-[11px] text-amber-700 dark:text-amber-400">
            {flagsUnknown
              ? tr("État des interrupteurs inconnu — chargement…", "Switch state unknown — loading…")
              : uploading
                ? tr("🔒 Verrouillé — analyse Whisper en cours", "🔒 Locked — Whisper is running")
                : skipWhisper
                  ? "⚠️ Whisper contourné — une transcription Fireflies est OBLIGATOIRE ; diarisation et identification faciale sont inactives"
                  : skipDiar
                    ? tr("⚠️ Diarisation coupée — les vidéos importées maintenant n'auront QU'UN SEUL locuteur, sans que rien ne l'indique sur la transcription", "⚠️ Diarization is off — videos imported now will have ONLY ONE speaker, with nothing on the transcript saying so")
                    : skipVisual
                      ? tr(
                          // Tâche 1325 — les locuteurs anonymes sont numérotés
                          // en LETTRES depuis l'unification du vocabulaire :
                          // annoncer « Speaker 1, 2… » promettait ici ce que
                          // la transcription n'affiche plus.
                          "⚠️ Identification faciale coupée — les locuteurs resteront anonymes (Speaker A, B…)",
                          "⚠️ Facial ID off — speakers will stay anonymous (Speaker A, B…)",
                        )
                      : tr("Tout est ON (par défaut)", "Everything is ON (default)")}
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm" data-testid="upload-error">{error}</div>
        )}

        {/* File upload area */}
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragActive ? "border-primary bg-primary/5 scale-[1.01]" :
            file ? "border-teal-500/30 bg-teal-500/5" :
            "border-border hover:border-primary/30"
          }`}
          onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
          data-testid="upload-dropzone"
        >
          <input
            ref={fileInputRef} type="file"
            accept={ACCEPT_MEDIA}
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            className="hidden" data-testid="video-file-input"
          />

          {file ? (
            <div className="flex items-center justify-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isAudioFile(file) ? "bg-violet-500/10" : "bg-teal-500/10"}`}>
                {isAudioFile(file) ? <MusicNote size={24} className="text-violet-400" /> : <VideoCamera size={24} className="text-teal-400" />}
              </div>
              <div className="text-left">
                <p className="font-medium text-sm">{file.name}</p>
                <p className="text-xs text-muted-foreground">{formatSize(file.size)} — {isAudioFile(file) ? "Audio" : "Video"}</p>
              </div>
              <button type="button" onClick={() => setFile(null)} className="p-2 hover:bg-secondary rounded-lg" data-testid="remove-file-btn">
                <X size={18} className="text-muted-foreground" />
              </button>
            </div>
          ) : (
            <>
              <CloudArrowUp size={40} className="mx-auto text-muted-foreground mb-3" />
              <p className="font-medium text-sm">
                <button type="button" onClick={() => fileInputRef.current?.click()} className="text-primary hover:underline" data-testid="browse-files-btn">
                  {t("click_to_upload")}
                </button>{" "}{t("or_drag_drop")}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{t("upload_formats")}</p>
            </>
          )}
        </div>

        {/* Title */}
        <div className="space-y-2">
          <Label htmlFor="title">{t("title_label")} *</Label>
          <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Formation vente - Module 3" required className="h-11" data-testid="video-title-input" />
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">{t("description_label")}</Label>
          <Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("description_placeholder")} rows={3} className="resize-none" data-testid="video-description-input" />
        </div>

        {/* Vincent #417 (Eric 2026-07-06 21h50 SPEC STRICK v2) — Fireflies
            DOIT être un FICHIER uploadé (pas un textarea). Elle ne remplace
            JAMAIS Whisper : servira à l'étape de fusion des transcriptions. */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <span>{lang === "fr" ? "Transcription Fireflies (optionnel)" : "Fireflies transcript (optional)"}</span>
            <span className="text-[10px] text-zinc-500 font-normal">— {lang === "fr" ? "servira à la fusion (Whisper reste actif)" : "used for fusion (Whisper still runs)"}</span>
          </Label>
          {/* Vincent #434 (Eric 2026-07-07 SPEC STRICK) — Drag & drop en plus
              du click. Cohérent avec le comportement de la zone d'upload
              principale de la vidéo. */}
          <div
            className="border-2 border-dashed border-zinc-200 dark:border-zinc-700 rounded-lg p-3 hover:border-[#002FA7]/40 transition-colors"
            data-testid="fireflies-dropzone"
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.add("border-[#002FA7]", "bg-blue-50/40"); }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); e.currentTarget.classList.remove("border-[#002FA7]", "bg-blue-50/40"); }}
            onDrop={(e) => {
              e.preventDefault(); e.stopPropagation();
              e.currentTarget.classList.remove("border-[#002FA7]", "bg-blue-50/40");
              const droppedFile = e.dataTransfer?.files?.[0];
              if (droppedFile) {
                const nameLower = droppedFile.name.toLowerCase();
                if (nameLower.endsWith(".docx") || nameLower.endsWith(".txt") || nameLower.endsWith(".md")) {
                  setFirefliesFile(droppedFile);
                }
              }
            }}
          >
            <input
              ref={firefliesInputRef}
              type="file"
              accept=".docx,.txt,.md,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown"
              onChange={(e) => setFirefliesFile(e.target.files?.[0] || null)}
              className="hidden"
              data-testid="fireflies-file-input"
            />
            {firefliesFile ? (
              <div className="flex items-center justify-between text-xs" data-testid="fireflies-file-selected">
                <span className="text-teal-700 dark:text-teal-400 font-semibold truncate">
                  ✓ {firefliesFile.name} ({(firefliesFile.size / 1024).toFixed(1)} Ko)
                </span>
                <button
                  type="button"
                  onClick={() => { setFirefliesFile(null); if (firefliesInputRef.current) firefliesInputRef.current.value = ""; }}
                  className="text-red-600 hover:underline"
                  data-testid="fireflies-file-remove"
                >
                  {lang === "fr" ? "Retirer" : "Remove"}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => firefliesInputRef.current?.click()}
                className="w-full text-xs text-zinc-500 hover:text-[#002FA7] py-2"
                data-testid="fireflies-file-button"
              >
                {lang === "fr"
                  ? "Cliquez ou déposez un fichier .docx / .txt / .md (Fireflies, Otter, Rev, Notebook LM...)"
                  : "Click or drop a .docx / .txt / .md file (Fireflies, Otter, Rev, Notebook LM...)"}
              </button>
            )}
          </div>
          <p className="text-[11px] text-zinc-400">
            {lang === "fr"
              ? "Cette transcription servira à l'étape de fusion des transcriptions. Whisper transcrit toujours de son côté."
              : "This transcript is used for the fusion step. Whisper still transcribes on its own."}
          </p>
        </div>

        {/* Thème */}
        <div className="space-y-2">
          <Label>{t("training_theme")}</Label>
          <Select value={theme} onValueChange={setTheme}>
            <SelectTrigger className="h-11" data-testid="training-theme-select">
              <SelectValue placeholder={t("select_theme")} />
            </SelectTrigger>
            <SelectContent>
              {TRAINING_THEMES.map((th) => (
                <SelectItem key={th.value} value={th.value} data-testid={`theme-${th.value}`}>{themeLabelFor(th, lang)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Vincent #406 (Eric 2026-07-02 SPEC STRICK) — Menu déroulant de
            diarisation : "1 seul locuteur connu" (attribution directe, skip
            pipeline diarisation) ou "1 ou plusieurs locuteurs à identifier". */}
        <div className="space-y-2">
          <Label>{lang === "fr" ? "Diarisation (locuteurs)" : "Diarization (speakers)"}</Label>
          <select
            value={diarizationMode}
            onChange={(e) => setDiarizationMode(e.target.value)}
            className="w-full h-11 rounded-md border border-input bg-background px-3 py-2 text-sm"
            data-testid="upload-diarization-mode"
          >
            <option value="multi">{lang === "fr" ? "identifier automatiquement les locuteurs" : "identify speakers automatically"}</option>
            <option value="single">{lang === "fr" ? "sélectionner 1 seul locuteur connu" : "select 1 known speaker"}</option>
            {/* Vincent XLSX 2026-07-16 Row 499 (Eric 2026-07-16 SPEC STRICK) — Nouvelle option */}
            <option value="multi_select">{lang === "fr" ? "sélectionner plusieurs locuteurs connus" : "select multiple known speakers"}</option>
          </select>
          {diarizationMode === "multi_select" && (
            <div className="pt-2 space-y-2" data-testid="upload-multi-select-panel">
              <Label>{lang === "fr" ? "Locuteurs présents dans cette vidéo (multi-sélection)" : "Speakers present in this video (multi-select)"}</Label>
              <p className="text-[10px] text-muted-foreground leading-snug">
                {lang === "fr"
                  ? "Cochez tous les locuteurs qui parlent dans cette vidéo. La diarisation IA se limitera à ces choix pour l'identification."
                  : "Check every speaker in this video. AI diarization will be constrained to these choices."}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto pr-1 border border-input rounded-md p-2 bg-background">
                {[...knownSpeakers]
                  .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "fr", { sensitivity: "base" }))
                  .map((s) => {
                    const checked = multiSelectedSpeakerIds.has(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-colors ${
                          checked ? "bg-teal-50 border border-teal-300" : "hover:bg-zinc-50 border border-transparent"
                        }`}
                        data-testid={`upload-multi-speaker-row-${s.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setMultiSelectedSpeakerIds((prev) => {
                              const next = new Set(prev);
                              if (next.has(s.id)) next.delete(s.id);
                              else next.add(s.id);
                              return next;
                            });
                          }}
                          className="h-3.5 w-3.5 accent-emerald-600"
                          data-testid={`upload-multi-speaker-check-${s.id}`}
                        />
                        <span className="truncate">
                          <strong>{s.name}</strong>
                          {s.role && <span className="text-muted-foreground ml-1">({s.role})</span>}
                        </span>
                      </label>
                    );
                  })}
              </div>
              {knownSpeakers.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  {lang === "fr"
                    ? "Aucun locuteur connu. Créez-en depuis « Locuteurs » d'abord."
                    : "No known speakers. Create some from « Speakers » first."}
                </p>
              )}
              <p className="text-[10px] text-muted-foreground" data-testid="upload-multi-speaker-count">
                {multiSelectedSpeakerIds.size} / {knownSpeakers.length}
              </p>
            </div>
          )}
          {diarizationMode === "single" && (
            <div className="pt-2">
              <Label htmlFor="upload-known-speaker">{lang === "fr" ? "Sélectionner le locuteur" : "Select the speaker"}</Label>
              {/* Vincent #405 (Eric 2026-07-02 SPEC STRICK) — Locuteurs triés A→Z. */}
              <select
                id="upload-known-speaker"
                value={singleSpeakerId}
                onChange={(e) => setSingleSpeakerId(e.target.value)}
                className="w-full h-11 rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                data-testid="upload-known-speaker-select"
              >
                <option value="">{lang === "fr" ? "— Choisir un locuteur —" : "— Choose a speaker —"}</option>
                {knownSpeakers.map((s) => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {knownSpeakers.length === 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {lang === "fr"
                    ? "Aucun locuteur connu. Créez-en depuis « Locuteurs » d'abord."
                    : "No known speakers. Create one from « Speakers » first."}
                </p>
              )}
            </div>
          )}
        </div>

        {/* Vincent #263 (2026-06-11) — Liste à COCHER (multi-sélection) des
            parcours auxquels rattacher la vidéo. Pas de menu déroulant :
            Vincent veut pouvoir cocher PLUSIEURS parcours. */}
        {pathways.length > 0 && (
          <div className="space-y-2">
            <Label>{lang === "fr" ? "Ajouter aux parcours (optionnel — sélection multiple)" : "Add to pathways (optional — multi-select)"}</Label>
            <div
              className="border border-border rounded-lg divide-y divide-border max-h-56 overflow-y-auto bg-background"
              data-testid="upload-pathway-checklist"
            >
              {/* Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3 types de parcours */}
              {["onboarding", "learning", "weekly"].map((ptype) => {
                // Vincent #294 (2026-06-12) — Tri A→Z des parcours par défaut
                // (aucun filtre dans cette UI). L'apprenant et l'éditeur
                // doivent voir un ordre prévisible.
                const subset = pathways
                  .filter((p) => p.pathway_type === ptype)
                  .slice()
                  .sort((a, b) =>
                    (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" })
                  );
                if (subset.length === 0) return null;
                return (
                  <div key={ptype} className="py-1">
                    <div className="px-3 py-1 text-[9px] uppercase text-muted-foreground font-bold tracking-wider">
                      {ptype === "onboarding"
                        ? (lang === "fr" ? "Parcours d'intégration" : "Onboarding pathways")
                        : ptype === "weekly"
                          ? (lang === "fr" ? "Formations hebdomadaires" : "Weekly trainings")
                          : (lang === "fr" ? "Parcours d'apprentissage" : "Learning pathways")}
                    </div>
                    {subset.map((p) => (
                      <label
                        key={p.id}
                        className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-muted/50 cursor-pointer"
                        data-testid={`upload-pathway-checkbox-${p.id}`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPathwayIds.includes(p.id)}
                          onChange={() =>
                            setSelectedPathwayIds((prev) =>
                              prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]
                            )
                          }
                          className="w-4 h-4 accent-[#002FA7]"
                        />
                        <span className="truncate flex-1">{p.name}</span>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {(p.video_ids || []).length} {lang === "fr" ? "vidéos" : "videos"}
                        </span>
                        {!p.is_published && (
                          <span className="text-[9px] bg-zinc-200 text-zinc-600 px-1 rounded">
                            {lang === "fr" ? "brouillon" : "draft"}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">
              💡 {lang === "fr"
                ? selectedPathwayIds.length > 0
                  ? `La vidéo sera automatiquement ajoutée aux ${selectedPathwayIds.length} parcours cochés après l'upload.`
                  : "Cochez un ou plusieurs parcours : la vidéo y sera automatiquement ajoutée après l'upload."
                : "Check one or more pathways: the video will be auto-appended after upload."}
            </p>
          </div>
        )}

        {/* Upload progress */}
        {uploading && (
          <div className="space-y-2" data-testid="upload-progress">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {t("uploading")}
                {file && file.size > CHUNK_THRESHOLD && (
                  <span className="ml-2 text-[11px] text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200" data-testid="chunked-mode-badge">
                    Mode chunké · {Math.ceil(file.size / CHUNK_SIZE)} × 10 Mo · {UPLOAD_CONCURRENCY} en parallèle
                  </span>
                )}
              </span>
              <span className="font-bold font-['Outfit'] flex items-center gap-2">
                {uploadProgress}%
                {uploadEta && (
                  <span className="text-[11px] font-normal text-zinc-500" data-testid="upload-eta">
                    · {uploadEta}
                  </span>
                )}
              </span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
            {file && file.size > CHUNK_THRESHOLD && (
              <p className="text-[11px] text-zinc-500">
                {tr(
                  `Envoi de ${(file.size / (1024 * 1024)).toFixed(0)} Mo en ${UPLOAD_CONCURRENCY} morceaux parallèles de 10 Mo, avec jusqu\u2019à 8 reprises automatiques par morceau. Si le navigateur plante, l\u2019envoi reprend tout seul au prochain chargement.`,
                  `Sending ${(file.size / (1024 * 1024)).toFixed(0)} MB as ${UPLOAD_CONCURRENCY} parallel 10 MB chunks, with up to 8 automatic retries per chunk. If the browser crashes, the upload resumes on its own next time you load the page.`,
                )}
              </p>
            )}
          </div>
        )}

        {/* Vincent 2026-07-21 (iter163 C) — Bannière de reprise d'upload
            détectée automatiquement au montage. */}
        {resumeInfo && !uploading && (
          <div
            className="bg-amber-50 border border-amber-300 rounded-xl p-4"
            data-testid="resume-upload-banner"
          >
            <p className="text-sm font-semibold text-amber-900 mb-1">
              &#9888; Un upload précédent a été interrompu
            </p>
            <p className="text-xs text-amber-800 mb-3">
              Fichier : <strong>{resumeInfo.filename}</strong> — {resumeInfo.receivedCount}/{resumeInfo.totalChunks} morceaux déjà envoyés.
              Sélectionne à nouveau le même fichier ci-dessus puis clique « Lancer l&apos;upload » pour reprendre là où ça s&apos;est arrêté.
            </p>
            <button
              onClick={() => {
                setResumeInfo(null);
                try { localStorage.removeItem(RESUME_STORAGE_KEY); } catch (_) { /* ignore */ }
              }}
              className="text-[11px] text-amber-800 underline hover:text-amber-900"
              data-testid="resume-cancel-btn"
            >
              Abandonner cette session (recommencer à zéro)
            </button>
          </div>
        )}

        {/* Pipeline info */}
        <div className="bg-card border border-border rounded-xl p-4">
          <h3 className="text-sm font-semibold mb-3">{t("what_happens_next")}</h3>
          <div className="space-y-2">
            {PIPELINE_STEPS.map((step, i) => (
                <div key={step.key} className="flex items-center gap-3 text-sm text-muted-foreground">
                  <div className="w-6 h-6 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-bold text-primary">{i + 1}</span>
                  </div>
                  {t(step.key)}
                </div>
              ))}
            </div>
        </div>

        {/* Submit */}
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={uploading || !file} className="flex-1 h-11 gap-2" data-testid="upload-submit-btn">
            {uploading ? <><SpinnerGap className="animate-spin" size={18} /> {t("uploading")}</> : <><CloudArrowUp size={18} /> {t("upload_and_process")}</>}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate("/dashboard")} className="h-11" data-testid="cancel-upload-btn">{t("cancel")}</Button>
        </div>
      </form>
    </div>
  );
}
